import { cn } from '@/lib/cn';
import { Button } from '@heidi/design-system/button';
import { HeidiLogoMark } from '@heidi/design-system/heidi-logo-mark';
import { TypographyCaption } from '@heidi/design-system/typography';
import { dictationIntlMessages } from '@heidi/dictate';
import { Square } from 'lucide-react';
import { useEffect, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';

export interface DictationProcessingContentProps {
  /** Whether the cancel button is visible (appears after 5s delay). */
  showCancel?: boolean;
  /** Called when the cancel/stop button is clicked. */
  onCancel?: () => void;
}

const STILL_PROCESSING_DELAY_MS = 5000;

export const DictationProcessingContent = ({
  showCancel: showCancelProp,
  onCancel,
}: DictationProcessingContentProps) => {
  const intl = useIntl();
  const [showCancelInternal, setShowCancelInternal] = useState(false);

  // Internal 5s delay timer when showCancel is not externally controlled
  useEffect(() => {
    if (showCancelProp !== undefined) return;

    const timer = setTimeout(() => {
      setShowCancelInternal(true);
    }, STILL_PROCESSING_DELAY_MS);

    return () => clearTimeout(timer);
  }, [showCancelProp]);

  const showCancel = showCancelProp ?? showCancelInternal;

  return (
    <div className="group flex flex-col items-center gap-2">
      <div className="relative size-6">
        <div
          className={cn(
            'absolute left-1/2 top-1/2 w-fit -translate-x-1/2 -translate-y-1/2 opacity-100 transition-opacity duration-200',
            showCancel && 'group-hover:opacity-0'
          )}
        >
          <HeidiLogoMark
            artwork="standard"
            aria-label="Heidi"
            className="size-6 animate-pulsate-spin"
          />
        </div>
        {showCancel && onCancel && (
          <Button
            iconOnly
            size="sm"
            variant="default"
            onClick={onCancel}
            aria-label={intl.formatMessage(dictationIntlMessages.cancelButton)}
            className="pointer-events-none absolute left-1/2 top-1/2 min-w-fit -translate-x-1/2 -translate-y-1/2 opacity-0 transition-opacity duration-200 ease-out group-hover:pointer-events-auto group-hover:opacity-100"
          >
            <Square fill="currentColor" />
          </Button>
        )}
      </div>
      {showCancel && (
        <TypographyCaption className="text-text-secondary">
          <FormattedMessage {...dictationIntlMessages.stillProcessing} />
        </TypographyCaption>
      )}
    </div>
  );
};
